// src/services/reportService.js
import ApiService from './api'
import AppURLs from '../config/urls'

class ReportService {
  // Helper function to get today's date range
  static getTodayDateRange() {
    const today = new Date()
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)
    
    return {
      startOfToday,
      endOfToday,
      todayDateString: today.toISOString().split('T')[0]
    }
  }

  // Helper function to check if a date is today
  static isToday(dateString) {
    const { todayDateString } = ReportService.getTodayDateRange()
    const orderDate = new Date(dateString)
    const orderDateString = orderDate.toISOString().split('T')[0]
    return orderDateString === todayDateString
  }

  // Get sales report data (filtered for today only)
  static async getSalesReport(branchId, date = null) {
    try {
      const { todayDateString } = ReportService.getTodayDateRange()
      const targetDate = date || todayDateString
      
      const params = {
        branch: branchId, // Use 'branch' instead of 'branchId' to match API
        startDate: targetDate,
        endDate: targetDate,
        orderStatus: 'completed', // Use 'orderStatus' instead of 'status'
        paymentStatus: 'paid' // Only paid orders
      }
      
      const response = await ApiService.get(AppURLs.ORDERS.GET_ALL, params)
      const data = response
      
      if (data.success && data.data?.orders) {
        let orders = data.data.orders
        
        // More strict filtering for today only (using UTC date comparison)
        const today = new Date()
        const todayDateString = today.toISOString().split('T')[0] // "2025-10-02"
        
        orders = orders.filter(order => {
          const orderDate = new Date(order.orderDate || order.createdAt)
          const orderDateString = orderDate.toISOString().split('T')[0] // Extract just the date part
          const isToday = orderDateString === todayDateString
          const isCompleted = order.orderStatus === 'completed'
          const isPaid = order.paymentStatus === 'paid'
          

          
          return isToday && isCompleted && isPaid
        })
        
        // Process orders for sales report with actual data structure
        const salesData = orders.map(order => ({
          orderId: order.orderNumber || order._id?.slice(-6) || 'N/A',
          amount: order.orderPayment?.totalAmount || 0,
          discount: order.orderPromotionEngineResult?.orderDetails?.totalDiscount || 0,
          originalAmount: order.orderPromotionEngineResult?.orderDetails?.originalOrderValue || 0,
          orderDate: order.orderDate || order.createdAt,
          orderType: order.orderType || 'N/A',
          customerName: order.customer?.name || 'Walk-in'
        }))

        // Calculate totals
        const totalSales = salesData.reduce((sum, order) => sum + order.amount, 0)
        const totalDiscounts = salesData.reduce((sum, order) => sum + order.discount, 0)
        const grossSales = salesData.reduce((sum, order) => sum + order.originalAmount, 0)



        return {
          success: true,
          data: {
            orders: salesData,
            totals: {
              totalSales,
              totalDiscounts,
              grossSales
            }
          }
        }
      }

      return { success: false, message: 'No sales data found' }
    } catch (error) {
      console.error('Error fetching sales report:', error)
      return { success: false, message: error.message }
    }
  }

    // Get returns report data (filtered for today only)
  static async getReturnsReport(branchId, date = null) {
    try {
      const { todayDateString } = ReportService.getTodayDateRange()
      const targetDate = date || todayDateString
      

      
      // Try to get actual returns data from API if endpoint exists
      // For now, return filtered mock data for today only
      const mockReturns = [
        {
          returnId: 'RET' + new Date().toISOString().slice(-6, -1),
          numberOfItems: 2,
          returnAmount: 150.00,
          returnDate: new Date().toISOString()
        },
        {
          returnId: 'RET' + (Date.now() - 1000000).toString().slice(-6),
          numberOfItems: 1,
          returnAmount: 75.50,
          returnDate: new Date().toISOString()
        }
      ]

      // Filter for today only
      const todayReturns = mockReturns.filter(returnItem => {
        return ReportService.isToday(returnItem.returnDate)
      })

      const totalReturns = todayReturns.reduce((sum, ret) => sum + ret.returnAmount, 0)
      const totalItems = todayReturns.reduce((sum, ret) => sum + ret.numberOfItems, 0)

      return {
        success: true,
        data: {
          returns: todayReturns,
          totals: {
            totalReturns,
            totalItems
          }
        }
      }
    } catch (error) {
      console.error('Error fetching returns report:', error)
      return { success: false, message: error.message }
    }
  }

  // Get stock report data using stock alerts endpoint
  static async getStockReport(branchId) {
    try {
      // Use stock alerts endpoint to get all stock data for the branch
      const response = await ApiService.get(AppURLs.STOCK_ALERTS.GET_ALL, { 
        branch: branchId 
      })
      
      if (response.success && response.data) {
        // Handle the actual API response structure from your example
        const items = response.data.items || []
        const summary = response.data.summary || {}
        
        const stockData = items.map(item => ({
          itemName: item.name || 'Unknown Item',
          itemCode: item.code || item.sku || 'N/A',
          type: item.itemType === 'raw_material' ? 'Raw Material' : 'Product',
          currentStock: item.currentStock || 0,
          threshold: item.threshold || 0,
          stockStatus: item.stockStatus || 'unknown',
          unit: item.unit?.abbreviation || item.unit?.name || 'units',
          category: item.category?.name || 'N/A'
        }))
        

        
        return {
          success: true,
          data: {
            items: stockData,
            summary: {
              totalItems: summary.totalItems || stockData.length,
              outOfStock: summary.outOfStock || 0,
              lowStock: summary.lowStock || 0,
              products: summary.products || 0,
              rawMaterials: summary.rawMaterials || 0
            }
          }
        }
      }
      

      return { 
        success: false, 
        message: 'No stock data found',
        data: { items: [], summary: { totalItems: 0, outOfStock: 0, lowStock: 0, products: 0, rawMaterials: 0 } }
      }
    } catch (error) {

      return { 
        success: false, 
        message: error.message,
        data: { items: [], summary: { totalItems: 0, outOfStock: 0, lowStock: 0, products: 0, rawMaterials: 0 } }
      }
    }
  }

  // Get branch details
  static async getBranchDetails(branchId) {
    try {
      const endpoint = ApiService.replaceUrlParams(AppURLs.BRANCHES.GET_BY_ID, { id: branchId })
      const response = await ApiService.get(endpoint)
      return response.success ? response.data : null
    } catch (error) {
      console.error('Error fetching branch details:', error)
      return null
    }
  }
}

export default ReportService